import { useCallback, useEffect, useRef, useState } from 'react';
import { BenchmarkRequestError, BenchmarkRunSummary, benchmarkService } from '../../../services/benchmarkService';

/**
 * A workflow's supplied-sample runs, newest first.
 *
 * `enabled` defers the request until the list is actually shown, so opening
 * the Backtest panel on institution history costs nothing extra.
 */
export function useSuppliedRuns({ families, projectId, mineOnly = false, enabled = true }: {
    families: string[];
    projectId?: string | null;
    /** Only the caller's own submissions. */
    mineOnly?: boolean;
    enabled?: boolean;
}) {
    const [runs, setRuns] = useState<BenchmarkRunSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loaded, setLoaded] = useState(false);
    // Only the latest request may write; an earlier one resolving late would
    // otherwise replace a newer list.
    const requestRef = useRef(0);

    const familyKey = families.join(',');

    const reload = useCallback(async () => {
        if (!familyKey) return;
        const request = ++requestRef.current;
        setLoading(true);
        setError(null);
        try {
            const data = await benchmarkService.listRuns({
                source: 'user_supplied',
                flow_family: familyKey.split(','),
                ...(projectId ? { project_id: projectId } : {}),
                ...(mineOnly ? { submitted_by: 'me' } : {})
            });
            if (request !== requestRef.current) return;
            setRuns(data?.runs ?? []);
            setLoaded(true);
        } catch (err) {
            if (request !== requestRef.current) return;
            setError(err instanceof BenchmarkRequestError ? err.message : 'Could not load supplied-sample runs.');
        } finally {
            if (request === requestRef.current) setLoading(false);
        }
    }, [familyKey, projectId, mineOnly]);

    useEffect(() => {
        if (enabled) reload();
    }, [enabled, reload]);

    return { runs, loading, error, loaded, reload };
}
